import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import socket from "../tools/socke";

const InstanciaEditable = ({ instancia, cierraModal }) => {
  const [formData, setFormData] = useState({
    ...instancia,
    estado: instancia.estado || "Activo",
    tipoInstancia: instancia.tipoInstancia || "Regular",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [materiasDisponibles, setMateriasDisponibles] = useState([]);
  const [materiasSeleccionadas, setMateriasSeleccionadas] = useState([]);
  const [dnisPorMateria, setDnisPorMateria] = useState({});
  const [esFinalExclusiva, setEsFinalExclusiva] = useState(false);

  const normalizarTexto = (valor = "") =>
    String(valor || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const esValorFinal = (valor) => {
    if (!valor) return false;

    if (typeof valor === "string") {
      const texto = normalizarTexto(valor);

      if (texto.includes("final")) return true;

      try {
        const json = JSON.parse(valor);
        return esValorFinal(json);
      } catch {
        return false;
      }
    }

    if (Array.isArray(valor)) {
      return valor.some((x) => esValorFinal(x));
    }

    if (typeof valor === "object") {
      return Object.values(valor).some((x) => esValorFinal(x));
    }

    return false;
  };

  const normalizarDnis = (texto = "") => {
    return String(texto)
      .split(/[\s,;]+/)
      .map((dni) => dni.trim())
      .filter((dni) => dni);
  };

  const textoDnisNormalizado = (texto = "") => {
    return normalizarDnis(texto).join(", ");
  };

  useEffect(() => {
    if (formData.exclusivo && String(formData.exclusivo).trim() !== "") {
      setFormData((prev) => ({
        ...prev,
        tipoInstancia: "Exclusiva",
      }));
    }
  }, [formData.exclusivo]);

  useEffect(() => {
    const obtenerMaterias = async () => {
      if (formData.tipoInstancia !== "Exclusiva" || !formData.nombreCurso) {
        setMateriasDisponibles([]);
        setMateriasSeleccionadas([]);
        setDnisPorMateria({});
        setEsFinalExclusiva(false);
        return;
      }

      try {
        const res = await axios.post(
          "https://wwwa.isepsantafe.net.ar/expedientes/calificaciones/devuelveMateriasPorCurso",
          { curso: formData.nombreCurso }
        );

        const materias = res.data.materias || [];
        setMateriasDisponibles(materias);

        if (!formData.exclusivo) {
          setMateriasSeleccionadas([]);
          setDnisPorMateria({});
          setEsFinalExclusiva(false);
          return;
        }

        if (esValorFinal(formData.exclusivo)) {
          setEsFinalExclusiva(true);
          setMateriasSeleccionadas([]);
          setDnisPorMateria({});
          return;
        }

        const exclusivoJSON =
          typeof formData.exclusivo === "string"
            ? JSON.parse(formData.exclusivo)
            : formData.exclusivo;

        const seleccionadas = Object.keys(exclusivoJSON || {});
        const valores = {};

        seleccionadas.forEach((materiaId) => {
          const valor = exclusivoJSON[materiaId];

          if (Array.isArray(valor)) {
            valores[materiaId] = valor.join(", ");
          } else {
            valores[materiaId] = String(valor || "");
          }
        });

        setEsFinalExclusiva(false);
        setMateriasSeleccionadas(seleccionadas);
        setDnisPorMateria(valores);
      } catch (error) {
        console.error("Error obteniendo materias:", error);
        Swal.fire("Error", "Error al obtener materias", "error");
        setMateriasDisponibles([]);
        setMateriasSeleccionadas([]);
        setDnisPorMateria({});
        setEsFinalExclusiva(false);
      }
    };

    obtenerMaterias();
  }, [formData.tipoInstancia, formData.nombreCurso, formData.exclusivo]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setIsEditing(true);
  };

  const handleTipoInstanciaChange = (e) => {
    const valor = e.target.value;

    setFormData((prev) => ({
      ...prev,
      tipoInstancia: valor,
    }));

    if (valor !== "Exclusiva") {
      setMateriasSeleccionadas([]);
      setDnisPorMateria({});
      setEsFinalExclusiva(false);
    }

    setIsEditing(true);
  };

  const handleMateriaChange = (e) => {
    const seleccion = Array.from(
      e.target.selectedOptions,
      (option) => option.value
    );

    setMateriasSeleccionadas(seleccion);

    setDnisPorMateria((prev) => {
      const nuevo = {};
      seleccion.forEach((id) => {
        nuevo[id] = prev[id] || "";
      });
      return nuevo;
    });

    setIsEditing(true);
  };

  const seleccionarTodasMaterias = () => {
    const todas = materiasDisponibles.map((m) => String(m.id));

    setMateriasSeleccionadas(todas);

    setDnisPorMateria((prev) => {
      const nuevo = {};
      todas.forEach((id) => {
        nuevo[id] = prev[id] || "";
      });
      return nuevo;
    });

    setIsEditing(true);
  };

  const limpiarMateriasSeleccionadas = () => {
    setMateriasSeleccionadas([]);
    setDnisPorMateria({});
    setIsEditing(true);
  };

  const handleDnisChange = (materiaId, value) => {
    setDnisPorMateria((prev) => ({
      ...prev,
      [materiaId]: value,
    }));

    setIsEditing(true);
  };

  const handleDnisBlur = (materiaId) => {
    setDnisPorMateria((prev) => ({
      ...prev,
      [materiaId]: textoDnisNormalizado(prev[materiaId] || ""),
    }));
  };

  const handleFinalChange = (checked) => {
    setEsFinalExclusiva(checked);

    if (checked) {
      setMateriasSeleccionadas([]);
      setDnisPorMateria({});
    }

    setIsEditing(true);
  };

  const armarExclusivoPayload = () => {
    if (esFinalExclusiva) {
      return "Final";
    }

    const exclusivo = {};

    materiasSeleccionadas.forEach((materiaId) => {
      exclusivo[materiaId] = normalizarDnis(dnisPorMateria[materiaId] || "");
    });

    return exclusivo;
  };

  const actualizaDatosInstancia = async (id, datos) => {
    try {
      const exclusivoFinal =
        datos.tipoInstancia === "Exclusiva" ? armarExclusivoPayload() : null;

      const payload = {
        ...datos,
        exclusivo:
          datos.tipoInstancia === "Exclusiva"
            ? esFinalExclusiva
              ? "Final"
              : JSON.stringify(exclusivoFinal)
            : null,
      };

      const resultado = await axios.post(
        `https://wwwa.isepsantafe.net.ar/expedientes/calificaciones/actualizaInstancia/${id}`,
        payload
      );

      if (resultado) {
        socket.emit("NovedadInstancia", "");

        Swal.fire({
          icon: "success",
          title: "Cambios guardados",
          text: "La instancia se actualizó correctamente.",
          confirmButtonColor: "#4caf50",
        }).then(() => {
          cierraModal();
        });
      }
    } catch (error) {
      console.error("Error actualizando instancia:", error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Hubo un problema al actualizar la instancia.",
        confirmButtonColor: "#d33",
      });
    }
  };

  const confirmarGuardado = () => {
    let resumenHtml = `
      <strong>Instancia:</strong> ${formData.nombreInstancia || ""}<br>
      <strong>Estado:</strong> ${formData.estado || "Activo"}<br>
      <strong>Tipo:</strong> ${formData.tipoInstancia || "Regular"}<br>
      <strong>Fecha inicio:</strong> ${formData.fechaInicio?.slice(0, 10) || ""}<br>
      <strong>Fecha final:</strong> ${formData.fechaFinal?.slice(0, 10) || ""}<br>
    `;

    if (formData.tipoInstancia === "Exclusiva") {
      if (esFinalExclusiva) {
        resumenHtml += `
          <br>
          <strong>Exclusivo:</strong> 
          <span style="color:#b45309;font-weight:bold;">Final</span><br>
        `;
      } else {
        resumenHtml += `<br><strong>Materias exclusivas:</strong><br>`;

        if (materiasSeleccionadas.length === 0) {
          resumenHtml += `<span style="color:red;">No hay materias seleccionadas.</span><br>`;
        }

        materiasSeleccionadas.forEach((materiaId) => {
          const materia = materiasDisponibles.find(
            (m) => String(m.id) === String(materiaId)
          );

          const dnis = normalizarDnis(dnisPorMateria[materiaId] || "");

          resumenHtml += `- ${materia?.nombreMateria || materiaId}: ${
            dnis.length
              ? dnis.join(", ")
              : "<span style='color:red;'>sin DNIs</span>"
          }<br>`;
        });
      }
    }

    Swal.fire({
      title: "¿Estás seguro?",
      html: resumenHtml,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#4caf50",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, guardar",
      cancelButtonText: "Cancelar",
      width: "650px",
    }).then((result) => {
      if (result.isConfirmed) {
        actualizaDatosInstancia(formData.id, formData);
      }
    });
  };

  return (
    <div
      style={{
        padding: "20px",
        fontFamily: "sans-serif",
        maxHeight: "80vh",
        overflowY: "auto",
      }}
    >
      <table
        style={{
          borderCollapse: "collapse",
          width: "100%",
          boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
        }}
      >
        <thead>
          <tr style={{ background: "#f5f5f5" }}>
            <th style={{ width: "150px", padding: "8px", border: "1px solid #ddd" }}>
              Nombre Instancia
            </th>
            <th style={{ padding: "8px", border: "1px solid #ddd" }}>
              Fecha Inicio
            </th>
            <th style={{ padding: "8px", border: "1px solid #ddd" }}>
              Fecha Finalización
            </th>
            <th style={{ padding: "8px", border: "1px solid #ddd" }}>
              Estado
            </th>
            <th style={{ padding: "8px", border: "1px solid #ddd" }}>
              Tipo Instancia
            </th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td style={{ border: "1px solid #ddd" }}>
              <input
                type="text"
                name="nombreInstancia"
                value={formData.nombreInstancia || ""}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "6px",
                  border: "none",
                  outline: "none",
                }}
              />
            </td>

            <td style={{ border: "1px solid #ddd" }}>
              <input
                type="date"
                name="fechaInicio"
                value={formData.fechaInicio?.slice(0, 10) || ""}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "6px",
                  border: "none",
                  outline: "none",
                }}
              />
            </td>

            <td style={{ border: "1px solid #ddd" }}>
              <input
                type="date"
                name="fechaFinal"
                value={formData.fechaFinal?.slice(0, 10) || ""}
                onChange={handleChange}
                style={{
                  width: "100%",
                  padding: "6px",
                  border: "none",
                  outline: "none",
                }}
              />
            </td>

            <td style={{ border: "1px solid #ddd" }}>
              <select
                name="estado"
                value={formData.estado || "Activo"}
                onChange={handleChange}
                style={{ width: "100%", padding: "6px" }}
              >
                <option value="Activo">Activa</option>
                <option value="Anulada">Anulada</option>
              </select>
            </td>

            <td style={{ border: "1px solid #ddd" }}>
              <select
                name="tipoInstancia"
                value={formData.tipoInstancia || "Regular"}
                onChange={handleTipoInstanciaChange}
                style={{ width: "100%", padding: "6px" }}
              >
                <option value="Regular">Regular</option>
                <option value="Exclusiva">Exclusiva</option>
              </select>
            </td>
          </tr>
        </tbody>
      </table>

      {formData.tipoInstancia === "Exclusiva" && (
        <div
          style={{
            marginTop: "20px",
            border: "1px solid #ddd",
            padding: "12px",
            borderRadius: "8px",
            background: "#fafafa",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "10px",
              flexWrap: "wrap",
              marginBottom: "10px",
            }}
          >
            <div>
              <strong>Configuración exclusiva</strong>
              <div style={{ fontSize: "13px", color: "#666" }}>
                Si marcás final, se enviará solamente la palabra Final.
              </div>
            </div>

            <label
              style={{
                display: "flex",
                gap: "8px",
                alignItems: "center",
                fontWeight: "bold",
                background: esFinalExclusiva ? "#fff3cd" : "#fff",
                padding: "6px 10px",
                borderRadius: "6px",
                border: "1px solid #ddd",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={esFinalExclusiva}
                onChange={(e) => handleFinalChange(e.target.checked)}
              />
              Es final
            </label>
          </div>

          {esFinalExclusiva ? (
            <div
              style={{
                marginTop: "12px",
                padding: "12px",
                borderRadius: "8px",
                background: "#fff3cd",
                border: "1px solid #ffeeba",
                color: "#856404",
                fontWeight: "bold",
              }}
            >
              Esta instancia se guardará como:
              <div style={{ marginTop: "6px", fontSize: "18px" }}>
                exclusivo = Final
              </div>
            </div>
          ) : materiasDisponibles.length > 0 ? (
            <>
              <div
                style={{
                  fontSize: "13px",
                  color: "#666",
                  marginBottom: "6px",
                }}
              >
                Seleccionadas: {materiasSeleccionadas.length} /{" "}
                {materiasDisponibles.length}
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  flexWrap: "wrap",
                  marginBottom: "8px",
                }}
              >
                <button
                  type="button"
                  onClick={seleccionarTodasMaterias}
                  style={{ padding: "6px 10px", cursor: "pointer" }}
                >
                  Seleccionar todas
                </button>

                <button
                  type="button"
                  onClick={limpiarMateriasSeleccionadas}
                  style={{ padding: "6px 10px", cursor: "pointer" }}
                >
                  Limpiar selección
                </button>
              </div>

              <select
                multiple
                value={materiasSeleccionadas}
                onChange={handleMateriaChange}
                style={{
                  minHeight: "130px",
                  width: "100%",
                  marginTop: "5px",
                }}
              >
                {materiasDisponibles.map((m) => (
                  <option key={m.id} value={String(m.id)}>
                    {m.nombreMateria}
                  </option>
                ))}
              </select>

              {materiasSeleccionadas.map((materiaId) => {
                const materia = materiasDisponibles.find(
                  (m) => String(m.id) === String(materiaId)
                );

                const dnisArray = normalizarDnis(
                  dnisPorMateria[materiaId] || ""
                );

                return (
                  <div
                    key={materiaId}
                    style={{
                      marginTop: "14px",
                      padding: "10px",
                      border: "1px solid #e2e2e2",
                      borderRadius: "8px",
                      background: "#fff",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "6px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={dnisArray.length > 0}
                        readOnly
                      />

                      <strong>
                        DNIs para {materia?.nombreMateria || materiaId}
                      </strong>

                      <span style={{ fontSize: "13px", color: "#666" }}>
                        ({dnisArray.length} DNI
                        {dnisArray.length === 1 ? "" : "s"})
                      </span>
                    </div>

                    <textarea
                      rows="4"
                      style={{ width: "100%", resize: "vertical" }}
                      value={dnisPorMateria[materiaId] || ""}
                      onChange={(e) =>
                        handleDnisChange(materiaId, e.target.value)
                      }
                      onBlur={() => handleDnisBlur(materiaId)}
                      placeholder={`Pegá DNIs separados por coma, espacio o salto de línea.
Ej:
30111222
30222333 30333444
30444555, 30555666`}
                    />

                    <small style={{ display: "block", marginTop: "5px" }}>
                      Al salir del campo se acomodan automáticamente separados
                      por coma.
                    </small>
                  </div>
                );
              })}
            </>
          ) : (
            <div style={{ color: "#777" }}>
              No se encontraron materias para este curso.
            </div>
          )}
        </div>
      )}

      {isEditing && (
        <div style={{ textAlign: "center", marginTop: "20px" }}>
          <button
            onClick={confirmarGuardado}
            style={{
              background: "#4caf50",
              color: "white",
              padding: "10px 20px",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "16px",
              boxShadow: "0 4px 6px rgba(0,0,0,0.2)",
            }}
          >
            💾 Guardar cambios
          </button>
        </div>
      )}
    </div>
  );
};

export default InstanciaEditable;